/**
 *
 * Details Service Sagas
 *
 */

import { take, call, put } from 'redux-saga/effects';
import moment from 'moment-timezone';
import {
  DEFAULT_DATE_FORMAT_TIMEZONE,
  DEFAULT_DATE_TIMEZONE,
  DEFAULT_DATE_FORMAT_SANS_TIME,
  EvaluatedActionType,
  TaxWithholdingType,
} from 'common';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import get from 'lodash/get';
import { searchRequest as recentDepositsSearchRequest } from 'services/recentDeposits';
import { atlasRequest } from 'services/atlasRequest';
import { addressRequest } from 'services/addressPreview';
import { notAsked, loading, success, failure } from './actions';
import { detailsRequest } from './api';
import { DETAILS_REQUEST } from './constants';

const MISSING = 'MISSING';

export function normalizeResponse(result) {
  const {
    amount,
    batchId,
    direction,
    hasActionAssignedToCurrentUser,
    hasActionResolvableByCurrentUser,
    prescribedActions,
    initiationTime,
    instructionSource,
    method,
    state,
    transferId,
    withdrawalDetails,
    journalDetails,
    accountDetails,
    achDetails,
    auditEvents,
    requestedAmount,
    wireWithdrawalDetails,
    wireDepositDetails,
    checkDetails,
    feeDetails,
    creditDetails,
    externalId,
    userActions,
    cashJournalDetails,
    fees,
    payPalDetails,
    depositDetails,
    positionDetails,
    rtpDetails,
    source,
  } = result;
  const openMarginCallPrescribedAction = prescribedActions.find(
    (action) => action.type === EvaluatedActionType.OpenMarginCall,
  );
  return {
    amount,
    auditEvents: sortAuditEvents(auditEvents),
    approvedBy: MISSING,
    bankAccountType: get(achDetails, 'bankAccount.type', ''),
    bankAccountNumber: rtpDetails
      ? get(rtpDetails, 'bankAccount.number', '')
      : get(achDetails, 'bankAccount.number', ''),
    batchId,
    achRelationshipId: achDetails ? achDetails.achRelationshipId : '',
    achRelationshipEstablishedDate: moment
      .tz(
        rtpDetails ? get(rtpDetails, 'achEstablishedDate', '') : get(achDetails, 'achEstablishedDate', ''),
        moment.ISO_8601,
        DEFAULT_DATE_TIMEZONE,
      )
      .format(DEFAULT_DATE_FORMAT_TIMEZONE),
    bankAccountIdentifierType: get(achDetails, 'bankAccount.identifier.type', ''),
    bankAccountIdentifierValue: rtpDetails
      ? get(rtpDetails, 'bankAccount.identifier.value', '')
      : get(achDetails, 'bankAccount.identifier.value', ''),
    bankConfirmationId: MISSING,
    accountIsActive: get(accountDetails, 'isActive', null),
    accountOpenedDate:
      accountDetails &&
      accountDetails.openedDate &&
      moment(accountDetails.openedDate, moment.ISO_8601).format(DEFAULT_DATE_FORMAT_SANS_TIME),
    apexAccountNumber: get(accountDetails, 'apexAccountNumber', ''),
    accountRegistration: get(accountDetails, 'accountRegistration', ''),
    correspondentCode: get(accountDetails, 'correspondent.code', ''),
    correspondentDeposit: get(accountDetails, 'correspondent.deposit', 0),
    correspondentName: get(accountDetails, 'correspondent.name', ''),
    date: moment.tz(initiationTime, moment.ISO_8601, DEFAULT_DATE_TIMEZONE).format(DEFAULT_DATE_FORMAT_TIMEZONE),
    openMarginCalls: get(openMarginCallPrescribedAction, 'context.openMarginCalls', []),
    destinationCountry: MISSING,
    destinationAccountNumber: MISSING,
    direction,
    disbursementType: MISSING,
    externalId: externalId || '',
    hasActionAssignedToCurrentUser,
    hasActionResolvableByCurrentUser,
    id: transferId,
    initiationTime,
    instructionSource,
    sourceType: get(source, 'type', ''),
    amountAvailableStartDayCashAvailable: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.startDayCashAvailable', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.startDayCashAvailable', 0),
    amountAvailablePendingDebitInterest: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.pendingDebitInterest', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.pendingDebitInterest', 0),
    amountAvailablePendingDebitDividends: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.pendingDebitDividends', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.pendingDebitDividends', 0),
    amountAvailableTotalDisbursements: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.totalDisbursements', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.totalDisbursements', 0),
    amountAvailableTotalDeposits: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.totalDeposits', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.totalDeposits', 0),
    amountAvailableTotal: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.total', 0)
      : get(cashJournalDetails, 'calculatedAmountAvailable.total', 0),
    amountAvailableAllowFullyPaidUnsettledFunds: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.includesFullyPaidUnsettledFunds')
      : get(cashJournalDetails, 'calculatedAmountAvailable.includesFullyPaidUnsettledFunds'),
    amountAvailableFullyPaidUnsettledFundsAmount: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.fullyPaidUnsettledFunds')
      : get(cashJournalDetails, 'calculatedAmountAvailable.fullyPaidUnsettledFunds'),
    amountRecentDepositThreshold: withdrawalDetails
      ? get(withdrawalDetails, 'calculatedAmountAvailable.recentDepositsThreshold')
      : get(cashJournalDetails, 'calculatedAmountAvailable.recentDepositsThreshold'),
    isFullDisbursement: withdrawalDetails
      ? get(withdrawalDetails, 'isFullDisbursement', false)
      : get(cashJournalDetails, 'isFullDisbursement', false),
    // IRA
    iraDistributionReason: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.distributionReason', '')
      : get(cashJournalDetails, 'iraDistributionDetails.distributionReason', ''),
    iraFederalTaxWithholdingType: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.federalTaxWithholding.valueType', TaxWithholdingType.Fixed)
      : get(cashJournalDetails, 'iraDistributionDetails.federalTaxWithholding.valueType', TaxWithholdingType.Fixed),
    iraFederalTaxWithholdingValue: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.federalTaxWithholding.value', 0)
      : get(cashJournalDetails, 'iraDistributionDetails.federalTaxWithholding.value', 0),
    iraStateTaxWithholdingValue: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.stateTaxWithholding.value', 0)
      : get(cashJournalDetails, 'iraDistributionDetails.stateTaxWithholding.value', 0),
    iraStateTaxWithholdingType: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.stateTaxWithholding.valueType', TaxWithholdingType.Fixed)
      : get(cashJournalDetails, 'iraDistributionDetails.stateTaxWithholding.valueType', TaxWithholdingType.Fixed),
    iraReceivingInstitutionName: withdrawalDetails
      ? get(withdrawalDetails, 'iraDistributionDetails.receivingInstitutionName', null)
      : get(cashJournalDetails, 'iraDistributionDetails.receivingInstitutionName', null),
    iraContributionDetailsType: depositDetails
      ? get(depositDetails, 'iraContributionDetails.type', null)
      : get(cashJournalDetails, 'iraContributionDetails.type', null),
    iraContributionApplyToPriorYear: depositDetails
      ? get(depositDetails, 'iraContributionDetails.applyToPriorYear', null)
      : get(cashJournalDetails, 'iraContributionDetails.applyToPriorYear', null),
    // JOURNAL DETAILS
    contraAccount: get(journalDetails, 'contraAccount', ''),
    programName: get(journalDetails, 'programName', ''),
    journalType: get(cashJournalDetails, 'journalType', ''),
    originatingInstitutionName: get(journalDetails, 'travelRules.originatingInstitutionName', ''),
    originatingInstitutionAccountNumber: get(journalDetails, 'travelRules.originatingInstitutionAccountNumber', ''),
    recipientInstitutionName: get(journalDetails, 'travelRules.recipientInstitutionName', ''),
    recipientInstitutionAccountNumber: get(journalDetails, 'travelRules.recipientInstitutionAccountNumber', ''),
    originatorFirstName: get(journalDetails, 'travelRules.originator.firstName', ''),
    originatorLastName: get(journalDetails, 'travelRules.originator.lastName', ''),
    originatorAddress: stringifyAddress(journalDetails, 'travelRules.originator.address', ''),
    originatorEntityName: get(journalDetails, 'travelRules.originator.entityName', ''),
    recipientFirstName: get(journalDetails, 'travelRules.recipient.firstName', ''),
    recipientLastName: get(journalDetails, 'travelRules.recipient.lastName', ''),
    recipientAddress: stringifyAddress(journalDetails, 'travelRules.recipient.address'),
    recipientEntityName: get(journalDetails, 'travelRules.recipient.entityName', ''),
    // END JOURNAL DETAILS
    method,
    prescribedActions,
    requestedAmount,
    sourceCountry: MISSING,
    state,
    type: MISSING,
    // WIRE WITHDRAWAL DETAILS
    ctsId: get(wireWithdrawalDetails, 'ctsId', null),
    bankConfirmation: get(wireWithdrawalDetails, 'bankConfirmation', null),
    payer: get(wireWithdrawalDetails, 'payer', null),
    payerAddress: stringifyAddress(wireWithdrawalDetails, 'payer.address'),
    beneficiary: get(wireWithdrawalDetails, 'beneficiary', null),
    beneficiaryAddress: stringifyAddress(wireWithdrawalDetails, 'beneficiary.address', null),
    destinationAccount: get(wireWithdrawalDetails, 'destinationAccount', null),
    destinationAddress: stringifyAddress(wireWithdrawalDetails, 'destinationAccount.address', null),
    furtherCreditTo: get(wireWithdrawalDetails, 'furtherCreditTo', null),
    // WIRE DEPOSIT DETAILS
    fedWireReferenceNumber: get(wireDepositDetails, 'fedWireReferenceNumber', null),
    houseBankAccountNumber: get(wireDepositDetails, 'houseBankAccountNumber', null),
    wireIsDomestic: get(wireDepositDetails, 'isDomestic', null),
    wireDepositBeneficiaryName: get(wireDepositDetails, 'beneficiary.name', null),
    wireDepositBeneficiaryAddress: stringifyAddress(wireDepositDetails, 'beneficiary.address', null),
    wireIsThirdParty: get(wireDepositDetails, 'beneficiary.isThirdParty', null),
    wireInstructions: get(wireDepositDetails, 'wireInstructions', []),
    wireDescriptions: get(wireDepositDetails, 'ledgerDescriptions', []),
    wireIsReturn: get(wireDepositDetails, 'isReturn', false),
    // END WIRE DETAILS
    checkCtsId: get(checkDetails, 'ctsId', null),
    checkPayer: get(checkDetails, 'payer', null),
    checkPayerAddress: stringifyAddress(checkDetails, 'payer.address', null),
    checkBeneficiary: get(checkDetails, 'beneficiary', null),
    checkBeneficiaryAddress: stringifyAddress(checkDetails, 'beneficiary.address', null),
    checkDeliveryMethod: get(checkDetails, 'deliveryMethod', null),
    checkMemo: get(checkDetails, 'memos', null),
    // END CHECK DETAILS
    feeContraAccount: get(feeDetails, 'contraAccount', null),
    feeType: get(feeDetails, 'type', null),
    feeAccountSideDescription: get(feeDetails, 'accountSideDescription', null),
    feeContraAccountSideDescription: get(feeDetails, 'contraAccountSideDescription', null),
    // END FEE DETAILS
    creditContraAccount: get(creditDetails, 'contraAccount', null),
    creditType: get(creditDetails, 'type', null),
    creditAccountSideDescription: get(creditDetails, 'accountSideDescription', null),
    creditContraAccountSideDescription: get(creditDetails, 'contraAccountSideDescription', null),
    // END CREDIT DETAILS
    cancel: get(userActions, 'cancel', false),
    createReviewAction: get(userActions, 'createReviewAction', false),
    reject: get(userActions, 'reject', false),
    resurrect: get(userActions, 'resurrect', false),
    suspend: get(userActions, 'suspend', false),
    updateAmount: get(userActions, 'updateAmount', false),
    updateWireDetails: get(userActions, 'updateWireDetails', false),
    hasReturn: get(userActions, 'return', false),
    fees,
    // PAYPAL DETAILS
    payPalStatus: get(payPalDetails, 'payPalStatus', null),
    payPalRelationshipId: get(payPalDetails, 'payPalRelationshipId', null),
    payPalEstablishedDate:
      get(payPalDetails, 'payPalEstablishedDate', null) &&
      moment
        .tz(get(payPalDetails, 'payPalEstablishedDate', null), moment.ISO_8601, DEFAULT_DATE_TIMEZONE)
        .format(DEFAULT_DATE_FORMAT_TIMEZONE),
    // POSITION DETAILS
    positionDetails,
    // RTP Details
    rtpTransactionId: get(rtpDetails, 'rtpTransactionId', '-'),
  };
}

export const stringifyAddress = (obj, path) => {
  const address = get(obj, path, null);
  const addressData = [];
  if (address != null) {
    const keys = ['streetAddress', 'city', 'state', 'country', 'postalCode'];
    keys.forEach((key) => {
      const value = get(address, key, null);
      if (value) {
        addressData.push(value);
      }
    });
  }
  return addressData.join(' ');
};

const sortAuditEvents = (obj) => {
  const auditEvents = [];
  obj.forEach((entry, index) => {
    auditEvents.push(entry);
    auditEvents[index].icon = entry.eventType;
  });
  return auditEvents.reverse();
};

export function* requestFlow(transferId, withRecentDeposits) {
  // Set the status to LOADING and store the params
  yield put(loading(transferId));

  try {
    // Make the request
    const result = yield call(detailsRequest, transferId);

    // Set the status to SUCCESS and store the result
    const normalizedResult = normalizeResponse(result);
    yield put(success(normalizedResult));

    // Kick off recentDepositsSearchRequest for the corresponding account if arg was provided.
    if (!withRecentDeposits || !transferId) return;
    if (!normalizedResult || !normalizedResult.apexAccountNumber) return;
    yield put(recentDepositsSearchRequest(normalizedResult.apexAccountNumber));
    yield put(atlasRequest(normalizedResult.apexAccountNumber));
    yield put(addressRequest(normalizedResult.apexAccountNumber));
  } catch (err) {
    let errorBody = err;
    if (isObject(err.response)) {
      if (isFunction(err.response.json)) {
        errorBody = yield err.response.json();
      }
      errorBody.status = err.response.status;
    } else {
      errorBody = errorBody.toString();
    }
    // The .json() method turns the response body into a promise.
    // Set status to FAILURE and pass along the error
    yield put(failure(errorBody));
  }
}

export function* detailsServiceFlow() {
  // Set the initial status
  yield put(notAsked());

  while (true) {
    // Watch for SEARCH_REQUEST to be dispatched
    const { transferId, withRecentDeposits } = yield take(DETAILS_REQUEST);

    // Make the request
    yield call(requestFlow, transferId, withRecentDeposits);
  }
}

export default [detailsServiceFlow()];



// WEBPACK FOOTER //
// ./src/services/details/sagas.js